import * as z from "zod";

/**
 * The report shape Edgy must return. Validated before anything is written to the
 * database, so a malformed model response fails loudly instead of persisting
 * garbage the UI would then have to defend against.
 */

const strengthSchema = z.object({
  title: z.string().min(1).max(120),
  detail: z.string().min(1).max(600),
  stat: z.string().max(120),
});

const leakSchema = z.object({
  title: z.string().min(1).max(120),
  detail: z.string().min(1).max(600),
  stat: z.string().max(120),
  estimatedCost: z.string().max(120),
});

const playbookGapSchema = z.object({
  rule: z.string().min(1).max(120),
  declared: z.string().max(200),
  actual: z.string().max(200),
  impact: z.string().max(300),
});

const weeklyActionSchema = z.object({
  action: z.string().min(1).max(300),
  checkMetric: z.string().max(160),
});

const actionFollowUpSchema = z.object({
  action: z.string().min(1).max(300),
  verdict: z.enum(["followed", "partially_followed", "not_followed", "unverifiable"]),
  stat: z.string().max(200),
});

/**
 * Present from the second analysis onwards. Optional so the first report — and every
 * report written before this block existed — still validates.
 */
const progressSchema = z.object({
  scoreDelta: z.number().int().min(-100).max(100),
  actionsFollowUp: z.array(actionFollowUpSchema).max(3),
});

export const edgeReportSchema = z.object({
  edgeScore: z.number().int().min(0).max(100),
  summary: z.string().min(1).max(400),
  progress: progressSchema.optional(),
  strengths: z.array(strengthSchema).min(1).max(3),
  leaks: z.array(leakSchema).min(1).max(3),
  playbookGaps: z.array(playbookGapSchema).max(3),
  weeklyActions: z.array(weeklyActionSchema).min(1).max(3),
  suggestedQuestions: z.array(z.string().min(1).max(200)).min(1).max(4),
});

export type EdgeReportContent = z.infer<typeof edgeReportSchema>;

const progressJsonSchema = {
  type: "object",
  description:
    "Comparison with the previous analysis. Use ONLY the figures supplied in the context's previousAnalysis block — verdicts are computed by the backend, never inferred.",
  properties: {
    // The API rejects minimum/maximum on integers ("For 'integer' type, properties
    // maximum, minimum are not supported"), so the range lives in the description.
    // zod still enforces it server-side (scoreDelta: int, -100..100).
    scoreDelta: { type: "integer", description: "Between -100 and 100." },
    actionsFollowUp: {
      type: "array",
      description: "At most 3 items.",
      items: {
        type: "object",
        properties: {
          action: { type: "string" },
          verdict: {
            type: "string",
            enum: ["followed", "partially_followed", "not_followed", "unverifiable"],
          },
          stat: { type: "string", description: "The backend-supplied figures, verbatim." },
        },
        required: ["action", "verdict", "stat"],
        additionalProperties: false,
      },
    },
  },
  required: ["scoreDelta", "actionsFollowUp"],
  additionalProperties: false,
} as const;

/** JSON Schema handed to the model so it returns this shape directly. */
export const edgeReportJsonSchema = {
  type: "object",
  properties: {
    // Range in the description, not as minimum/maximum: the API rejects those on
    // integers. zod still enforces 0..100 when the response is parsed.
    edgeScore: { type: "integer", description: "Between 0 and 100." },
    summary: { type: "string", description: "One sentence." },
    strengths: {
      type: "array",
      description: "Between 2 and 3 items.",
      items: {
        type: "object",
        properties: {
          title: { type: "string" },
          detail: { type: "string" },
          stat: { type: "string", description: "A figure taken verbatim from the context." },
        },
        required: ["title", "detail", "stat"],
        additionalProperties: false,
      },
    },
    leaks: {
      type: "array",
      description: "Between 2 and 3 items.",
      items: {
        type: "object",
        properties: {
          title: { type: "string" },
          detail: { type: "string" },
          stat: { type: "string" },
          estimatedCost: { type: "string", description: "Cost of this leak, from context figures only." },
        },
        required: ["title", "detail", "stat", "estimatedCost"],
        additionalProperties: false,
      },
    },
    playbookGaps: {
      type: "array",
      description: "At most 3 items.",
      items: {
        type: "object",
        properties: {
          rule: { type: "string" },
          declared: { type: "string" },
          actual: { type: "string" },
          impact: { type: "string" },
        },
        required: ["rule", "declared", "actual", "impact"],
        additionalProperties: false,
      },
    },
    weeklyActions: {
      type: "array",
      description: "Between 1 and 3 items.",
      items: {
        type: "object",
        properties: {
          action: { type: "string" },
          checkMetric: { type: "string" },
        },
        required: ["action", "checkMetric"],
        additionalProperties: false,
      },
    },
    suggestedQuestions: {
      type: "array",
      description: "Between 3 and 4 items.",
      items: { type: "string" },
    },
  },
  required: [
    "edgeScore",
    "summary",
    "strengths",
    "leaks",
    "playbookGaps",
    "weeklyActions",
    "suggestedQuestions",
  ],
  additionalProperties: false,
} as const;

/**
 * Schema for a follow-up analysis. `progress` is required here — the trader has a
 * previous report, so the comparison must be produced rather than left to chance.
 */
export const edgeReportWithProgressJsonSchema = {
  ...edgeReportJsonSchema,
  properties: {
    ...edgeReportJsonSchema.properties,
    progress: progressJsonSchema,
  },
  required: [...edgeReportJsonSchema.required, "progress"],
} as const;
